import { applyHeadlineToUnitPrice } from '@supply-mover-2-0/core';
import type {
	ItemPromoHeadline,
	PortalStorefrontItemCardDto,
} from '@supply-mover-2-0/shared';
import {
	ImagePlaceholder,
	ResilientImage,
	useTranslation,
} from '@supply-mover-2-0/web-ui';
import { useDisplaySettings } from '../../../hooks/useDisplaySettings';
import { formatCurrency } from '../../../lib/formatCurrency';
import { AddToCartControl } from '../../AddToCartControl';

/**
 * Shared item card used by every storefront grid that lists shoppable
 * items — dashboard ItemCarousel rows, the promo detail page, future
 * "search results" / "items you bought" surfaces. Encapsulates the
 * full catalog-card visual treatment: image, top-left discount pill,
 * floating add-to-cart, title, manufacturer, promo-aware price.
 *
 * Layout shape is controlled by the caller via the wrapper element
 * (carousel uses fixed widths + scroll-snap; grid uses CSS-grid
 * cells). The card's own width is `w-full` so it fills whatever cell
 * it's dropped into, and the inner image area is a `aspect-square`
 * for consistent proportions across surfaces.
 */
export function StorefrontItemCard({
	item,
	onNavigate,
	onLikelyStaleUrl,
}: {
	item: PortalStorefrontItemCardDto;
	onNavigate: () => void;
	onLikelyStaleUrl?: () => void;
}): JSX.Element {
	const { t } = useTranslation();
	return (
		<div
			role="button"
			tabIndex={0}
			onClick={onNavigate}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					onNavigate();
				}
			}}
			className="w-full text-left bg-brand-card border border-brand-border rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-portal-accent"
		>
			<div className="relative aspect-square bg-brand-input flex items-center justify-center">
				<ResilientImage
					src={item.primaryImageUrl}
					alt={item.description}
					className="w-full h-full object-contain"
					loading="lazy"
					onLikelyStaleUrl={onLikelyStaleUrl}
					fallback={<ImagePlaceholder />}
				/>
				{item.promoHeadline != null &&
					item.promoHeadline.displayBadgeInLists && (
						<span className="absolute top-1 left-1 px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-900 text-[10px] sm:text-xs font-bold shadow-sm">
							{t(item.promoHeadline.badgeText)}
						</span>
					)}
				<div className="absolute bottom-1 right-1 z-10">
					<AddToCartControl
						itemId={item.id}
						variant="floating"
						inStock={item.inStock}
					/>
				</div>
			</div>
			<div className="p-2 space-y-1">
				<p className="text-xs font-medium text-gray-900 dark:text-gray-100 line-clamp-2 min-h-[2.25rem]">
					{t(item.description)}
				</p>
				{item.manufacturer != null && (
					<p className="text-[11px] text-brand-muted truncate">
						{t(item.manufacturer)}
					</p>
				)}
				<StorefrontItemPrice
					price={item.price}
					contractPrice={item.contractPrice}
					promoHeadline={item.promoHeadline}
				/>
			</div>
		</div>
	);
}

/**
 * Promo-aware price block for the storefront item card. Falls back to
 * `<></>` when the price is unknown so the price slot collapses
 * cleanly instead of showing a stub.
 */
export function StorefrontItemPrice({
	price,
	contractPrice,
	promoHeadline,
}: {
	price: string | null;
	contractPrice: string | null;
	promoHeadline: ItemPromoHeadline | null;
}): JSX.Element {
	const { t } = useTranslation();
	const displaySettings = useDisplaySettings();
	// PD-14239: hide the price element entirely when the active client's
	// per-user hidePricing flag is on (canSeePricing=false). The server
	// already nulls price/contractPrice; this removes the slot so no empty
	// price area remains on the card.
	if (!displaySettings.canSeePricing) {
		return <></>;
	}
	const formattedPrice = formatCurrency(price);
	const formattedContract = formatCurrency(contractPrice);
	const display = formattedContract ?? formattedPrice;
	if (display == null) return <></>;

	const listNum = price != null ? parseFloat(price) : NaN;
	const contractNum = contractPrice != null ? parseFloat(contractPrice) : NaN;
	const basePrice = Number.isFinite(contractNum)
		? contractNum
		: Number.isFinite(listNum)
			? listNum
			: NaN;
	const promoFinal =
		Number.isFinite(basePrice) && promoHeadline != null
			? applyHeadlineToUnitPrice(basePrice, promoHeadline)
			: null;
	const promoActive = promoFinal != null && promoFinal < basePrice;

	if (promoActive && promoHeadline != null) {
		return (
			<div className="flex items-center gap-1 flex-wrap">
				<span className="inline-flex items-baseline px-1.5 py-0.5 rounded-md bg-amber-100 dark:bg-amber-900/30 text-gray-900 dark:text-amber-100 text-xs font-bold">
					{t(formatCurrency(promoFinal) ?? '')}
				</span>
				<span className="text-[10px] text-gray-400 line-through">
					{t(formatCurrency(basePrice) ?? '')}
				</span>
			</div>
		);
	}

	return (
		<p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
			{t(formatCurrency(basePrice) ?? '')}
		</p>
	);
}
